"use client";

import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CancelIcon from "@mui/icons-material/CancelRounded";
import CheckIcon from "@mui/icons-material/DoneRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import {
  filterPartnerOptions,
  groupByInitial,
  moveHighlight,
  partnerLabel,
  type PartnerOption,
} from "@/lib/partnerOptions";

interface PartnerSwitcherProps {
  options: PartnerOption[];
  activeCode: string | null;
  activeName: string | null;
  onSelect: (code: string) => void;
}

const optionId = (code: string) => `partner-option-${code}`;
const LISTBOX_ID = "partner-switcher-listbox";

export function PartnerSwitcher({ options, activeCode, activeName, onSelect }: Readonly<PartnerSwitcherProps>) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const hasKeyboard = useMediaQuery("(hover: hover) and (pointer: fine)");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1);

  const open = Boolean(anchor);
  const searching = query.trim().length > 0;
  const filtered = useMemo(() => filterPartnerOptions(options, query), [options, query]);
  const groups = useMemo(() => (searching ? [] : groupByInitial(filtered)), [searching, filtered]);

  const close = () => {
    setAnchor(null);
    setQuery("");
  };

  const select = (code: string) => {
    close();
    if (code !== activeCode) onSelect(code);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setHighlight(options.findIndex((o) => o.partnerCode === activeCode));
    setAnchor(e.currentTarget);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setHighlight(value.trim() ? 0 : options.findIndex((o) => o.partnerCode === activeCode));
  };

  useEffect(() => {
    if (!open || highlight < 0) return;
    const code = filtered[highlight]?.partnerCode;
    if (code) document.getElementById(optionId(code))?.scrollIntoView({ block: "nearest" });
  }, [open, highlight, filtered]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => moveHighlight(h, e.key === "ArrowDown" ? 1 : -1, filtered.length));
    } else if (e.key === "Enter" && highlight >= 0 && filtered[highlight]) {
      e.preventDefault();
      select(filtered[highlight].partnerCode);
    }
  };

  const renderOption = (option: PartnerOption, index: number) => {
    const selected = option.partnerCode === activeCode;
    return (
      <Box
        key={option.partnerCode}
        id={optionId(option.partnerCode)}
        role="option"
        aria-selected={selected}
        onClick={() => select(option.partnerCode)}
        onMouseMove={() => highlight !== index && setHighlight(index)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          cursor: "pointer",
          borderRadius: "10px",
          py: 1,
          pl: 1,
          pr: selected ? 0 : "10px",
          bgcolor: selected ? "action.selected" : index === highlight ? "action.hover" : "transparent",
          outline: index === highlight ? "1px solid" : "none",
          outlineColor: "action.focus",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
          <Typography
            sx={{ fontSize: 15, lineHeight: "24px", color: "text.primary", fontWeight: selected ? 600 : 400, overflowWrap: "anywhere" }}
          >
            {partnerLabel(option)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "secondary.dark" }}>{option.partnerCode}</Typography>
        </Box>
        {selected && <CheckIcon sx={{ color: "primary.main", mr: "12px" }} />}
      </Box>
    );
  };

  const searchField = (
    <TextField
      autoFocus
      fullWidth
      label={fullScreen ? "Search partners" : "Search partner"}
      placeholder="Type partner name or code"
      value={query}
      onChange={(e) => handleQueryChange(e.target.value)}
      onKeyDown={handleKeyDown}
      slotProps={{
        htmlInput: {
          role: "combobox",
          "aria-expanded": true,
          "aria-controls": LISTBOX_ID,
          "aria-activedescendant": filtered[highlight] ? optionId(filtered[highlight].partnerCode) : undefined,
          "aria-autocomplete": "list",
        },
        inputLabel: { shrink: true },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "warning.dark" }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {searching ? (
                <IconButton size="small" aria-label="clear search" onClick={() => handleQueryChange("")}>
                  <CloseIcon fontSize="small" sx={{ color: "primary.main" }} />
                </IconButton>
              ) : (
                <ArrowDropUpIcon sx={{ color: "primary.main" }} />
              )}
            </InputAdornment>
          ),
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "rgba(85, 204, 204, 0.76)", borderWidth: 2 },
      }}
    />
  );

  const summary = (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 20 }}>
      <Typography sx={{ fontSize: 11, color: "secondary.light" }}>
        {searching ? `${filtered.length} matching partners` : `${options.length} partners · A–Z`}
      </Typography>
      {searching && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.5,
            borderRadius: "24px",
            bgcolor: "action.selected",
            maxWidth: "60%",
          }}
        >
          <Typography noWrap sx={{ fontSize: 11, color: "secondary.light" }}>
            Search: “{query.trim()}”
          </Typography>
          <IconButton size="small" aria-label="remove search filter" onClick={() => handleQueryChange("")} sx={{ p: 0 }}>
            <CancelIcon sx={{ fontSize: 20, color: "text.disabled" }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  let runningIndex = 0;
  const list = (
    <Box
      id={LISTBOX_ID}
      role="listbox"
      aria-label="Partners"
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        borderTop: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.23)",
        mx: -2,
        px: 2,
        pt: 1.5,
        pb: "calc(16px + env(safe-area-inset-bottom))",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      {filtered.length === 0 && (
        <Typography sx={{ fontSize: 14, color: "text.secondary", py: 2 }}>No partners match your search.</Typography>
      )}
      {searching
        ? filtered.map((option, i) => renderOption(option, i))
        : groups.map((group) => (
            <Box key={group.letter} sx={{ display: "flex", flexDirection: "column", gap: "4px", pt: 1 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "info.dark" }}>{group.letter}</Typography>
              {group.options.map((option) => renderOption(option, runningIndex++))}
            </Box>
          ))}
    </Box>
  );

  const hint = hasKeyboard && !fullScreen && (
    <Box sx={{ borderTop: "1px solid", borderColor: "rgba(0, 0, 0, 0.23)", mx: -2, px: 2, pt: 1.1, pb: 1 }}>
      <Typography sx={{ fontSize: 10, color: "secondary.light" }}>Use ↑ ↓ to move · Enter to select · Esc to close</Typography>
    </Box>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1, minHeight: 0, px: 2, pt: 3, pb: 1 }}>
      {fullScreen && (
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.6, color: "text.primary" }}>
              Select partner
            </Typography>
            {activeName && (
              <Typography sx={{ fontSize: 12, color: "text.primary" }}>Current: {activeName}</Typography>
            )}
          </Box>
          <IconButton aria-label="close" onClick={close} sx={{ mr: 0.5 }}>
            <CloseIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </Box>
      )}
      {searchField}
      {summary}
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, maxHeight: fullScreen ? "none" : 360 }}>
        {list}
      </Box>
      {hint}
    </Box>
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="switch partner"
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          color: "warning.dark",
          bgcolor: "white",
          borderRadius: "24px",
          px: 1,
          py: 0.75,
          boxShadow: 2,
          "&:hover": { bgcolor: "grey.100" },
        }}
      >
        <BusinessIcon sx={{ fontSize: 20, color: "warning.dark" }} />
        <ArrowDropDownIcon sx={{ fontSize: 20, color: "primary.main" }} />
      </IconButton>

      {fullScreen ? (
        <Dialog fullScreen open={open} onClose={close} aria-label="Select partner">
          {content}
        </Dialog>
      ) : (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                width: 478,
                maxWidth: "calc(100vw - 32px)",
                borderRadius: "14px",
                boxShadow: "0px 7px 12px 0px rgba(0, 0, 0, 0.25)",
                mt: 1,
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          {content}
        </Popover>
      )}
    </>
  );
}
